/**
 * Per-field last-write-wins clocks (conventions §14), shared by the Worker and the web app so both
 * compare exactly the same way.
 */

/** `{ fieldName: clockKey(at, mutationId) }`, the same shape as @tahi/db's FieldClock. */
type FieldClock = Record<string, string>;

/** A device clock this far ahead of the server is treated as wrong and clamped. */
export const MAX_AHEAD_MS = 5 * 60 * 1000;

/** Managed by the server; never part of last-write-wins. */
const UNCLOCKED = new Set(['createdAt', 'updatedAt']);

export function readClock(raw: unknown): FieldClock {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== 'object') return {};
  const out: FieldClock = {};
  for (const [k, v] of Object.entries(value)) if (typeof v === 'string') out[k] = v;
  return out;
}

/**
 * A field's clock entry: `${at}|${mutationId}`. `at` is always toISOString() form (fixed width),
 * so plain string order is time order, and equal times fall back to the mutation id. Every device
 * and the server compare the same strings, so a tie has the same winner everywhere.
 */
export function clockKey(at: string, mutationId: string): string {
  return `${at}|${mutationId}`;
}

/** The `at` half of a seed's clock entries: older than any real mutation time. */
export const SEED_CLOCK_AT = '0000-01-01T00:00:00.000Z';

/**
 * The clock entry an insert-if-absent (`ifAbsent`, a seeded default) gives every column of the row
 * it creates. It sorts before every real clock key, so any real edit wins over seeded values on
 * every device, even one made earlier than the seed. Without it, a device that seeds before it has
 * pulled would keep its seed over an older rename or delete made on another device.
 */
export function seedClockKey(mutationId: string): string {
  return clockKey(SEED_CLOCK_AT, mutationId);
}

/** The clock key for the columns a newly inserted row gets (conventions §14). */
export function createClockKey(at: string, m: { id: string; ifAbsent?: true }): string {
  return m.ifAbsent ? seedClockKey(m.id) : clockKey(at, m.id);
}

/** The device clock that made `at` runs too far ahead of the server: the server will clamp it. */
export function isTooFarAhead(at: string, now: Date): boolean {
  return Date.parse(at) - now.getTime() > MAX_AHEAD_MS;
}

/**
 * The mutation time the server uses: normalised, and clamped when too far in the future. A clamped
 * time is `now`, or 1 ms after `after` (the time of the mutation the same device sent just before)
 * when that is later, so clamping never reorders one device's mutations: "+1 then Undo" stays in
 * that order instead of tying and falling back to the random mutation ids.
 */
export function effectiveAt(at: string, now: Date, after: string | null = null): string {
  const t = Date.parse(at);
  if (!isTooFarAhead(at, now)) return new Date(t).toISOString();
  const floor = after === null ? 0 : Date.parse(after) + 1;
  return new Date(Math.max(now.getTime(), floor)).toISOString();
}

export interface MergeResult {
  win: Record<string, unknown>;
  lose: Record<string, unknown>;
  clock: FieldClock;
}

/**
 * Per-field last-write-wins: a field takes the incoming value when `key` is greater than its clock
 * entry, or than `fallback` (the row's createdAt) when it has none. An equal key is the same
 * mutation again and changes nothing.
 */
export function mergeFields(
  clock: FieldClock,
  values: Record<string, unknown>,
  key: string,
  fallback: string,
): MergeResult {
  const next: FieldClock = { ...clock };
  const win: Record<string, unknown> = {};
  const lose: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(values)) {
    if (UNCLOCKED.has(field)) continue;
    if (key > (next[field] ?? fallback)) {
      win[field] = value;
      next[field] = key;
    } else {
      lose[field] = value;
    }
  }
  return { win, lose, clock: next };
}
